import {EMPTY_VALUE as EMPTY} from '@consts/common';

import {JobParamElement, JobTemplateDto, LocalizedTitle} from './types';

export const SCHEDULE_TYPES = [
    {
        id: '1',
        code: '0 0 0 1/1 * ? *',
        title: {en: 'Daily', ru: 'Ежедневно', fr: 'Quotidien'},
    },
    {
        id: '2',
        code: '0 0 0 ? * 1 *',
        title: {en: 'Weekly', ru: 'Еженедельно', fr: 'Hebdomadaire'},
    },
    {
        id: '3',
        code: '0 0 0 1 1/1 ? *',
        title: {en: 'Monthly', ru: 'Ежемесячно', fr: 'Mensuel'},
    },
    {
        id: '4',
        code: '0 0 0 1 */3 ? *',
        title: {en: 'Quarterly', ru: 'Ежеквартально', fr: 'Trimestriel'},
    },
];

export const localizeTitle = (title: LocalizedTitle | null | undefined, language: string) => {
    if (!title) return EMPTY;
    return title[language] ?? title.en ?? Object.values(title)[0] ?? EMPTY;
};

const parseParamsSchema = (schema: JobTemplateDto['paramsSchema']) => {
    if (!schema) return null;
    if (typeof schema !== 'string') return schema;

    try {
        return JSON.parse(schema);
    } catch {
        return null;
    }
};

const findPropertyByName = (obj: unknown, propName: string): any => {
    if (!obj || typeof obj !== 'object') return null;

    const record = obj as Record<string, unknown>;
    if (Object.prototype.hasOwnProperty.call(record, propName)) {
        return record[propName];
    }

    for (const key of Object.keys(record)) {
        const result = findPropertyByName(record[key], propName);
        if (result) return result;
    }

    return null;
};

export const parseJobParams = (job: JobTemplateDto | null): JobParamElement[] => {
    if (!job?.params) return [];

    const schema = parseParamsSchema(job.paramsSchema);

    return job.params
        .split('\n')
        .map((param) => param.trim())
        .filter(Boolean)
        .map((param) => param.split(':'))
        .map(([key, ...valueParts]) => ({
            key: key?.trim(),
            value: valueParts.join(':').trim(),
        }))
        .filter((param) => param.key && param.value)
        .map((param) => {
            const leaf = findPropertyByName(schema, param.key);
            return {
                key: param.key,
                value: param.value,
                type: leaf?.type === 'integer' ? 'number' : 'text',
                title: leaf?.title ?? null,
            };
        });
};

export const buildParams = (sourceParams: string, values: Record<string, string>) => {
    const allParams = sourceParams
        .split('\n')
        .map((param) => param.trim())
        .filter(Boolean);
    const baseParam = allParams[0];
    const restParams = Object.entries(values).map(([key, value]) => `${key}: ${value}`);

    return [baseParam, ...restParams].filter(Boolean).join('\n ');
};
